import { TKV2_ORDER_ATTACHMENT_API, isDefined } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import {
  useGetTkv2ReconciliationAttachmentAPI,
  usePutTkv2ReconciliationAttachmentAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { DEFAULT_SYNC_DRIVER_DELAY_MS } from './consts.ts';
import { reconciliationMutationComplete, setReconciliation } from '../../redux/features/reconciliationSlice.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';

export const useReconciliationSyncDriver = (orderID: string) => {
  const dispatch = useAppDispatch();
  const { mutate: reconciliationMutate } = usePutTkv2ReconciliationAttachmentAPI();
  const { data: persistedReconciliationData } = useGetTkv2ReconciliationAttachmentAPI({
    pathParams: { orderID },
  });
  const reconciliation = useAppSelector(selectReconciliation);
  const { editRevision = 0, serverRevision = 0 } = reconciliation?.syncState ?? {};

  useEffect(() => {
    if (!persistedReconciliationData || isDefined(reconciliation.syncState)) return;

    const parsedData =
      TKV2_ORDER_ATTACHMENT_API.RECONCILIATION.GET.RESPONSE.parse(persistedReconciliationData);
    dispatch(setReconciliation(parsedData.body));
  }, [persistedReconciliationData]);

  const onSuccess = () => {
    dispatch(
      reconciliationMutationComplete({
        status: 'success',
        serverRevision: editRevision,
      }),
    );
  };

  const onError = () => {
    dispatch(
      reconciliationMutationComplete({
        status: 'error',
      }),
    );
  };

  const mutate = () =>
    reconciliationMutate(
      {
        pathParams: { orderID },
        body: TKV2_ORDER_ATTACHMENT_API.RECONCILIATION.PUT.REQUEST.BODY.parse(reconciliation),
      },
      { onSuccess, onError },
    );

  const mutationTrigger = useEdgeTrigger({
    condition: editRevision > serverRevision,
    delayMs: DEFAULT_SYNC_DRIVER_DELAY_MS,
    action: mutate,
  });

  useEffect(() => {
    mutationTrigger.restartTimer();
  }, [editRevision, mutationTrigger]);
};
